import React, { FormEvent, useEffect, useState } from "react";
import { btn } from "../../../shared/styles/buttonStyles";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { ListBulletIcon } from "@heroicons/react/24/outline";
import { useLoading } from "../../../shared/state/LoadingContext";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "../../../shared/utils/apiError";
import { migrationApplication, migrationListPath } from "../migrationRoutes";
import FeatureTabs, { FeatureTab } from "../../../shared/ui/FeatureTabs";
import FeatureInformationPanel from "../../../shared/ui/FeatureInformationPanel";
import ConfirmActionDialog from "../../../shared/ui/ConfirmActionDialog";
import { getAccessToken } from "../../../shared/auth/getAccessToken";

type migrationForm = {
  instanceId: string;
  server: string;
  schoolCode: string;
};

const emptyForm: migrationForm = { instanceId: "", server: "", schoolCode: "" };

const labelClassName = "block min-w-0 text-xs font-semibold leading-4 text-slate-700";
const hintClassName = "mt-1 text-[11px] leading-4 text-slate-500";
const groupClassName = "workspace-subcard min-w-0 space-y-3 border border-slate-200 bg-slate-50 p-3";

const MigrationCreate = () => {
  const server = ["A", "B", "C", "D", "E"];
  const navigate = useNavigate();
  const { loading, setLoading } = useLoading();
  const [migrationData, setMigrationData] = useState<migrationForm>(emptyForm);
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<FeatureTab>("operations");
  const [sourceServer, setSourceServer] = useState("");

  const apiUrl = process.env.REACT_APP_API_URL;

  // Read-only, so the operator sees where the data is copied from
  useEffect(() => {
    const loadSourceServer = async () => {
      try {
        const accessToken = await getAccessToken();
        const response = await axios.get(`${apiUrl}/migration/source`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setSourceServer(response.data?.server ?? "");
      } catch {
        setSourceServer("");
      }
    };
    void loadSourceServer();
  }, [apiUrl]);

  // The run imports into the production GradeXpert DB and transfers to Xuno, so it is confirmed first
  const requestMigration = (event: FormEvent) => {
    event.preventDefault();
    if (!migrationData.instanceId || !migrationData.schoolCode || !migrationData.server) {
      toast.error("Fill in all migration details.");
      return;
    }

    setShowConfirm(true);
  };

  const createMigration = async () => {
    setShowConfirm(false);

    try {
      setLoading(true);
      const accessToken = await getAccessToken();

      const response = await axios.post(
        `${apiUrl}/migration/${migrationApplication}`,
        {
          instanceId: migrationData.instanceId,
          server: migrationData.server,
          schoolCode: migrationData.schoolCode,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      // API returns 200 with success false when it can't create the migration
      if (!response.data?.success) {
        toast.error(response.data?.errorPhrase || "Failed to create migration.");
        return;
      }

      toast.success("Migration has been queued.");
      setMigrationData(emptyForm);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create migration."));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setMigrationData({
      ...migrationData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="feature-workspace">
      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "information" && (
        <FeatureInformationPanel summary="Copies one school from GradeXpert into a Xuno instance.">
          <h3 className="text-sm font-semibold text-slate-800">How to run a migration</h3>
          <ol className="mt-3 list-decimal space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Check the Source Server. It is fixed by the API and cannot be changed here.</li>
            <li>Enter the 6-character GradeXpert School Code.</li>
            <li>Choose the Xuno MySQL server (A to E) that hosts the instance, then enter the 6-character Xuno Instance ID.</li>
            <li>Select <span className="font-semibold">Create Migration</span> and confirm.</li>
            <li>Open <span className="font-semibold">List</span> to follow it. Open a row to see each step and its log.</li>
          </ol>
          <h3 className="mt-4 text-sm font-semibold text-slate-800">What it does</h3>
          <ol className="mt-3 list-decimal space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Backs up the school&apos;s GradeXpert database.</li>
            <li>Restores it into the production GradeXpert database, replacing the school&apos;s existing copy there.</li>
            <li>Transfers the data into the Xuno instance.</li>
          </ol>
          <h3 className="mt-4 text-sm font-semibold text-slate-800">Good to know</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>This is Production. It changes a live GradeXpert database and a live Xuno instance.</li>
            <li>It runs in the background. Closing the page does not stop it.</li>
            <li>If a step fails, the run stops and shows Failed. Open the row for the log.</li>
          </ul>
        </FeatureInformationPanel>
      )}

      {/* Hidden instead of unmounted so typed form input survives a tab switch */}
      <div className="mt-2" id="feature-panel-operations" role="tabpanel" aria-labelledby="feature-tab-operations" tabIndex={0} hidden={activeTab !== "operations"}>
        <section className="workspace-card min-w-0 w-full overflow-hidden border border-slate-200 bg-white px-5 py-4">
          {/* List stays outside the form so it never submits it */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold leading-5 text-slate-800">Create Migration</h3>
              <p className="mt-1 text-[11px] leading-4 text-slate-500">
                Queues a migration to the target Xuno instance.
              </p>
            </div>
            <button
              type="button"
              className={`flex items-center gap-1 ${btn.secondarySmall}`}
              onClick={() => {
                navigate(migrationListPath);
              }}
            >
              <ListBulletIcon className="h-4 w-4"></ListBulletIcon>
              List
            </button>
          </div>
          <hr className="my-3 border-slate-200" />
          <form
            onSubmit={requestMigration}
            id="migrationForm"
            className="space-y-3"
          >
            {/* Reads left to right like the data moves: from GradeXpert, to Xuno */}
            <div className="grid max-w-3xl grid-cols-1 gap-3 md:grid-cols-2">
              <div role="group" aria-labelledby="migrationFromTitle" className={groupClassName}>
                <h4 id="migrationFromTitle" className="text-xs font-bold uppercase tracking-wide text-slate-500">From GradeXpert</h4>
                <div>
                  <span className={labelClassName}>Source Server</span>
                  <div className="mt-1 break-all text-xs text-slate-600">{sourceServer || "Not available"}</div>
                  <p className={hintClassName}>Fixed by the API.</p>
                </div>
                <div>
                  <label htmlFor="schoolCode" className={labelClassName}>School Code</label>
                  <input
                    type="text"
                    id="schoolCode"
                    name="schoolCode"
                    value={migrationData.schoolCode}
                    onChange={handleChange}
                    required
                    minLength={6}
                    maxLength={6}
                    autoComplete="off"
                    aria-describedby="schoolCodeHint"
                    className="mt-1 block w-full"
                  />
                  <p id="schoolCodeHint" className={hintClassName}>6 letters or digits, the code of the school in GradeXpert.</p>
                </div>
              </div>

              <div role="group" aria-labelledby="migrationToTitle" className={groupClassName}>
                <h4 id="migrationToTitle" className="text-xs font-bold uppercase tracking-wide text-slate-500">To Xuno</h4>
                <div>
                  <label htmlFor="server" className={labelClassName}>Xuno MySQL Server</label>
                  <select
                    id="server"
                    name="server"
                    value={migrationData.server}
                    onChange={handleChange}
                    required
                    aria-describedby="serverHint"
                    className="mt-1 block w-full"
                  >
                    <option value="">Select a server</option>
                    {server.map((value) => (
                      <option key={value} value={value}>
                        Server {value}
                      </option>
                    ))}
                  </select>
                  <p id="serverHint" className={hintClassName}>The server that hosts the instance.</p>
                </div>
                <div>
                  <label htmlFor="instanceId" className={labelClassName}>Xuno Instance ID</label>
                  <input
                    type="text"
                    id="instanceId"
                    name="instanceId"
                    value={migrationData.instanceId}
                    onChange={handleChange}
                    required
                    minLength={6}
                    maxLength={6}
                    autoComplete="off"
                    aria-describedby="instanceIdHint"
                    className="mt-1 block w-full"
                  />
                  <p id="instanceIdHint" className={hintClassName}>6 letters or digits, the instance to receive the data.</p>
                </div>
              </div>
            </div>
            <button
              type="submit"
              className={`${btn.applySmall} w-[8.5rem] md:h-9`}
              disabled={loading}
            >
              {loading ? "Queueing..." : "Create Migration"}
            </button>
          </form>
        </section>
      </div>

      <ConfirmActionDialog
        open={showConfirm}
        title="Confirm Migration"
        description="Queue this GradeXpert migration?"
        confirmLabel="Create Migration"
        destructive
        onConfirm={() => void createMigration()}
        onCancel={() => setShowConfirm(false)}
      >
        <div className="space-y-2 text-xs text-slate-700">
          <ul className="list-disc space-y-1 pl-4">
            <li>Restores the school&apos;s GradeXpert data into the production GradeXpert database.</li>
            <li>Then transfers the data to the target Xuno instance.</li>
          </ul>
          <div className="workspace-subcard space-y-1 border border-slate-200 bg-slate-50 p-3">
            <div><span className="font-medium text-slate-500">Source Server: </span>{sourceServer || "Not available"}</div>
            <div><span className="font-medium text-slate-500">School Code: </span>{migrationData.schoolCode}</div>
            <div><span className="font-medium text-slate-500">Target Server: </span>{migrationData.server}</div>
            <div><span className="font-medium text-slate-500">Xuno Instance ID: </span>{migrationData.instanceId}</div>
          </div>
        </div>
      </ConfirmActionDialog>
    </div>
  );
};

export default MigrationCreate;
